import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ChevronRight, Package, User } from "lucide-react";
import { NAV_LINKS, CONSULTATION_URL } from "@/data/constants";
import { useAuth } from "@/context/AuthContext";

/**
 * FLOWTALIS wordmark — gold, wide letterspacing, minimal, executive.
 * Reference: attached image with FLOWTALIS™ in gold Manrope.
 */
const FlowtalisWordmark = ({ variant = "light", size = "md" }) => {
  const sizeClasses = size === "lg" ? "text-[1.75rem]" : size === "sm" ? "text-[0.95rem]" : "text-[1.35rem]";
  const color = variant === "dark" ? "#c8a24a" : "#c8a24a";
  return (
    <span
      data-testid="brand-wordmark"
      className={`font-bold tracking-[0.22em] ${sizeClasses} leading-none`}
      style={{ color, fontFamily: "Manrope, sans-serif" }}
    >
      FLOWTALIS<span className="text-[0.55em] align-super ml-0.5 font-medium">™</span>
    </span>
  );
};

const Header = () => {
  const [open, setOpen] = useState(false);
  const { user, isAdmin, logout } = useAuth();
  const nav = useNavigate();

  return (
    <header className="sticky top-0 z-50">
      {/* Top black strip — brand promise + auth CTAs */}
      <div className="bg-[var(--ft-black)] text-white/85">
        <div className="ft-container flex h-11 items-center justify-between text-[0.75rem]">
          <div className="hidden md:flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Package size={14} strokeWidth={1.5} style={{ color: "var(--ft-gold)" }} />
              <span className="tracking-[0.22em] font-semibold" style={{ color: "var(--ft-gold)" }}>FROM SOURCE TO BOTTLE™</span>
            </div>
            <span className="text-white/25">|</span>
            <span className="tracking-[0.18em] text-white/70 font-medium">YOUR VISION. OUR EXPERTISE. PREMIUM RESULTS.</span>
          </div>
          <div className="md:hidden">
            <span className="tracking-[0.22em] font-semibold text-xs" style={{ color: "var(--ft-gold)" }}>FROM SOURCE TO BOTTLE™</span>
          </div>
          <div className="flex items-center gap-4">
            {user && (
              <>
                <button onClick={() => nav(isAdmin ? "/admin" : "/portal")} data-testid="header-portal-btn"
                        className="hidden sm:flex items-center gap-1.5 hover:text-[var(--ft-gold)] transition-colors">
                  <User size={13} strokeWidth={1.5} /> {isAdmin ? "Admin" : "Portal"}
                </button>
                <button onClick={() => { logout(); nav("/"); }} data-testid="header-logout-btn" className="hover:text-[var(--ft-gold)] transition-colors">Sign out</button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main white nav with gold wordmark */}
      <div className="bg-white border-b border-[var(--ft-border)]">
        <div className="ft-container flex h-[72px] items-center justify-between">
          <Link to="/" data-testid="header-logo-link" className="flex items-center">
            <FlowtalisWordmark />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `text-[0.9375rem] font-medium transition-colors ${
                    isActive ? "text-[var(--ft-gold)]" : "text-[var(--ft-charcoal)] hover:text-[var(--ft-gold)]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center">
            <a
              href={CONSULTATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-book-main"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold border-2 border-[var(--ft-gold)] text-[var(--ft-gold)] hover:bg-[var(--ft-gold)] hover:text-white transition-all"
            >
              Book a Consultation <ChevronRight size={14} />
            </a>
          </div>

          <button
            className="lg:hidden p-2 text-[var(--ft-charcoal)]"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-[var(--ft-border)] bg-white">
            <div className="ft-container py-5 flex flex-col gap-2">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.to.replace("/", "")}`}
                  className="py-2.5 text-[var(--ft-charcoal)] hover:text-[var(--ft-gold)] text-[0.9375rem] font-medium"
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="flex flex-col gap-2 pt-4 mt-2 border-t border-[var(--ft-border)]">
                {user ? (
                  <>
                    <button onClick={() => { setOpen(false); nav(isAdmin ? "/admin" : "/portal"); }} className="text-left py-2 text-[var(--ft-charcoal)]" data-testid="mobile-portal-btn">
                      {isAdmin ? "Admin Dashboard" : "Client Portal"}
                    </button>
                    <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="text-left py-2 text-[var(--ft-muted)]" data-testid="mobile-logout-btn">Sign out</button>
                  </>
                ) : (
                  <a href={CONSULTATION_URL} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border-2 border-[var(--ft-gold)] text-[var(--ft-gold)] w-fit" data-testid="mobile-book-btn">Book Consultation</a>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
export { FlowtalisWordmark };
